import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../api'
import CloseIcon from '../../assets/icons/JSX/CloseIcon'
import UploadFileIcon from '../../assets/icons/JSX/UploadFileIcon'
import Colors from '../../assets/styles/Colors'

interface UploadFileProps {
  label?: string
  hideLabel?: boolean
  errorMessage?: string
  defaultFileName?: string
  onChange: (value: string) => void
  style?: React.CSSProperties
}

const UploadFile: React.FC<UploadFileProps> = ({
  hideLabel = false,
  label = 'please enter label text',
  errorMessage,
  defaultFileName,
  onChange,
  style,
}) => {
  const { t } = useTranslation()
  const [uploadFileMutation, { isLoading: isUploadFileLoading }] = api.useUploadFileMutation()
  const [fileUrl, setFileUrl] = useState<string | undefined>(defaultFileName)

  // Sync with defaultFileName prop changes
  useEffect(() => {
    if (defaultFileName !== undefined) {
      setFileUrl(defaultFileName)
    }
  }, [defaultFileName])

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    try {
      const result = await uploadFileMutation({ file }).unwrap()
      const uploadedUrl = result.url

      setFileUrl(uploadedUrl)
      onChange(uploadedUrl)

      // Reset file input to allow uploading the same file again
      event.target.value = ''
    } catch (error) {
      toast.error('გთხოვთ, აირჩიოთ ფაილი')
      console.info('File upload error:', error)
    }
  }

  const extractFileName = (url: string): string => {
    try {
      const urlObj = new URL(url)
      const pathSegments = urlObj.pathname.split('/')
      const fileName = pathSegments[pathSegments.length - 1]

      // Remove UUID prefix if exists (format: uuid-filename.ext)
      const parts = fileName.split('-')
      if (parts.length > 1) {
        return parts.slice(1).join('-')
      }

      return fileName || url
    } catch (error) {
      // If URL parsing fails, try to extract from pathname directly
      const pathSegments = url.split('/')
      return pathSegments[pathSegments.length - 1] || url
    }
  }

  const handleOpenFile = () => {
    if (!fileUrl) return
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
  }

  const handleClearFile = () => {
    setFileUrl(undefined)
    onChange('')
  }

  return (
    <Wrapper style={style}>
      {!hideLabel && <Label>{label}</Label>}

      <Box>
        <Input type="file" onChange={handleFileChange} />
        <UploadBox $hasFile={!!fileUrl}>
          {isUploadFileLoading ? (
            <LoadingText>{t('loading')}</LoadingText>
          ) : fileUrl ? (
            <UploadedFile>
              <FileName onClick={handleOpenFile} title={fileUrl}>
                {extractFileName(fileUrl)}
              </FileName>
              <CloseIcon onClick={handleClearFile} />
            </UploadedFile>
          ) : (
            <UploadPlaceholder>
              <UploadFileIcon />
              <span>{t('uploadFile')}</span>
            </UploadPlaceholder>
          )}
        </UploadBox>
      </Box>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Wrapper>
  )
}
// Styled Components
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const Label = styled.label`
  font-size: 14px;
  color: ${Colors.primaryGrey};
  margin-bottom: 12px;
`

const Box = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  background-color: ${Colors.white};
  -webkit-box-shadow: 0px 0px 15px 3px #88888811;
  box-shadow: 0px 0px 15px 3px #88888811;
  border-radius: 4px;
`

const Input = styled.input`
  height: 48px;
  width: 100%;
  background-color: ${Colors.white};
  border-radius: 4px;
  z-index: 1;
`

const UploadBox = styled.div<{ $hasFile: boolean }>`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  background-color: ${Colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  pointer-events: ${({ $hasFile }) => ($hasFile ? 'all' : 'none')};
  border: 1px solid ${Colors.primaryBlack};
`

const UploadPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  span {
    font-size: 14px;
    line-height: 20px;
    color: ${Colors.primaryBlack};
    pointer-events: none;
  }

  svg {
    color: ${Colors.primaryBlack};
    height: 16px;
    width: fit-content;
    background-color: ${Colors.white};
    pointer-events: none;
  }
`

const UploadedFile = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-inline: 20px;
  gap: 12px;

  svg {
    width: 13px !important;
    height: 13px !important;
    cursor: pointer;
    z-index: 3;
    flex-shrink: 0;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }
`

const FileName = styled.span`
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.primaryBlack};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: ${Colors.primaryGrey};
  }

  &:active {
    opacity: 0.8;
  }
`

const LoadingText = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.primaryBlack};
  pointer-events: none;
`

const ErrorMessage = styled.div`
  font-size: 14px;
  color: ${Colors.primaryRed};
  margin-top: 10px;
`

export default UploadFile
